import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (
      productForm &&
      typeof productForm.artieShouldBlockAddForPackFields === 'function' &&
      productForm.artieShouldBlockAddForPackFields()
    ) {
      event.preventDefault();
      return;
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /**
   * @typedef {{ variantId: string, quantity: number, properties?: Record<string, string>, artieLineRole?: 'main' | 'system' | 'secondary', sellingPlanId?: string }} ArtieCartQueueLine
   */

  /** @type {ArtieCartQueueLine[]} */
  #addToCartQueue = [];

  /** @type {{ items?: unknown[], item_count?: number } | null} */
  #mealCartSnapshot = null;

  /** @type {Promise<{ items?: unknown[], item_count?: number } | null> | null} */
  #mealCartFetchPromise = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    this.#invalidateMealCartSnapshot();

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  #invalidateMealCartSnapshot() {
    this.#mealCartSnapshot = null;
    this.#mealCartFetchPromise = null;
  }

  /**
   * Theme-rendered cart line count (drawer / page). Returns 0 when explicitly empty.
   * @returns {number}
   */
  #getThemeCartItemCount() {
    let max = 0;
    let found = false;
    for (const el of document.querySelectorAll('cart-items-component [ref="cartItemCount"]')) {
      const n = parseInt(String(el.textContent ?? '').trim(), 10);
      if (Number.isNaN(n)) continue;
      found = true;
      if (n > max) max = n;
    }
    return found ? max : -1;
  }

  /** @returns {number} */
  #countDomCartLineRows() {
    return document.querySelectorAll('cart-items-component tr.cart-items__table-row[data-key]').length;
  }

  /**
   * Build a cart-like snapshot from visible cart row markup (no `/cart.js`).
   * @returns {{ items: unknown[], item_count: number }}
   */
  #buildCartSnapshotFromDom() {
    /** @type {unknown[]} */
    const items = [];

    for (const row of document.querySelectorAll(
      'cart-items-component tr.cart-items__table-row[data-key]'
    )) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      const key = row.dataset.key?.trim();
      if (!key) continue;

      const variantId = row.dataset.variantId?.trim() ?? '';
      const dogId = row.dataset.dogId?.trim() ?? '';
      const dogName = row.dataset.dogName?.trim() ?? '';
      const artieLine = row.getAttribute('data-artie-line')?.trim() ?? '';

      /** @type {Record<string, string>} */
      const properties = {};
      if (artieLine) properties._artie_line = artieLine;
      else if (this.#isArtieMainMealCartDomRow(row)) properties._artie_line = 'main';
      if (dogId) properties._dog_id = dogId;
      if (dogName) properties.dog = dogName;
      if (variantId) properties._artie_sync_main = variantId;

      items.push({
        key,
        variant_id: variantId ? Number(variantId) : undefined,
        properties,
      });
    }

    const themeCount = this.#getThemeCartItemCount();
    const itemCount = themeCount >= 0 ? themeCount : items.length;
    return { items, item_count: itemCount };
  }

  /**
   * When the rendered cart row count matches the theme count, DOM rows are complete (drawer open).
   * @returns {boolean}
   */
  #isDomCartSnapshotAuthoritative() {
    const themeCount = this.#getThemeCartItemCount();
    const domRows = this.#countDomCartLineRows();
    if (themeCount === 0 && domRows === 0) return true;
    if (themeCount > 0 && themeCount === domRows) return true;
    if (themeCount < 0 && domRows > 0) return true;
    return false;
  }

  /**
   * Prefer visible cart markup; one shared `/cart.js` fetch when the drawer is closed or counts disagree.
   * @param {Array<Record<string, string>>} [entries]
   * @returns {Promise<{ items?: unknown[], item_count?: number } | null>}
   */
  async #resolveCartSnapshotForMealAtc(entries = []) {
    const themeCount = this.#getThemeCartItemCount();
    const domRows = this.#countDomCartLineRows();

    if (themeCount === 0 && domRows === 0) {
      const empty = { items: [], item_count: 0 };
      this.#mealCartSnapshot = empty;
      return empty;
    }

    if (this.#isDomCartSnapshotAuthoritative()) {
      const domCart = this.#buildCartSnapshotFromDom();
      this.#mealCartSnapshot = domCart;
      return domCart;
    }

    return this.#fetchCartForMealSync();
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        if (this.#blockAddIfPackFieldsIncomplete()) return;

        void (async () => {
          if (this.#shouldUseMultipleDogAtc()) {
            const atcEntries = this.#getMultipleDogAtcEntries();
            const [, cartPreload] = await Promise.all([
              this.#syncMultipleDogAtcBeforeAdd(),
              this.#resolveCartSnapshotForMealAtc(atcEntries),
            ]);
            const multiDogLines = this.#buildCartLinesFromMultipleDogAtc(quantity, cartPreload);
            if (multiDogLines && multiDogLines.length > 0) {
              for (const line of multiDogLines) {
                this.#addToCartQueue.push(line);
              }
              this.refs.addToCartButtonContainer?.animateAddToCart?.();
              return;
            }
            if (atcEntries.length) return;
          }
          const syncLines = this.#getArtieSyncLineItems(intendedVariantId, quantity);
          if (syncLines && syncLines.length > 1) {
            for (const line of syncLines) {
              this.#addToCartQueue.push(line);
            }
          } else {
            this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
          }
          this.refs.addToCartButtonContainer?.animateAddToCart?.();
        })();
        return;
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @returns {Element | null}
   */
  #findArtieSyncSearchRoot() {
    return (
      this.closest('.shopify-section') ||
      this.closest('[id^="shopify-section-"]') ||
      this.closest('.product-information') ||
      this.closest('.product-details')
    );
  }

  /**
   * Pack builder on the same PDP section as this form (if any).
   * @returns {HTMLElement | null}
   */
  #findPackBuilderForProductForm() {
    const root = this.#findArtieSyncSearchRoot() || document.documentElement;
    const pack = root.querySelector('pack-builder') || document.querySelector('pack-builder');
    return pack instanceof HTMLElement ? pack : null;
  }

  /**
   * Active dog row inputs for cart `properties.dog` / weight checks (active row, else first row).
   * @returns {{ row: HTMLElement | null, nameInput: HTMLInputElement | null, weightInput: HTMLInputElement | null }}
   */
  #getPackBuilderActiveRowInputs() {
    const pack = this.#findPackBuilderForProductForm();
    if (!pack) return { row: null, nameInput: null, weightInput: null };
    const activeRow = pack.querySelector('.artie-pack-builder__dog-row[data-active="true"]');
    let row = activeRow instanceof HTMLElement ? activeRow : null;
    if (!row) {
      const first = pack.querySelector('.artie-pack-builder__dog-row');
      row = first instanceof HTMLElement ? first : null;
    }
    if (!row) return { row: null, nameInput: null, weightInput: null };
    const nameEl =
      row.querySelector('.artie-pack-builder__input-name') || row.querySelector('.js-dog-name');
    const weightEl = row.querySelector('.artie-pack-builder__input-weight');
    return {
      row,
      nameInput: nameEl instanceof HTMLInputElement ? nameEl : null,
      weightInput: weightEl instanceof HTMLInputElement ? weightEl : null,
    };
  }

  /**
   * @returns {Element | null}
   */
  #getArtieProductSystemSyncCard() {
    const root = this.#findArtieSyncSearchRoot();
    if (!root) return null;
    return root.querySelector('product-system-card[js-data-add-to-cart-sync-product="true"]');
  }

  /**
   * Selected dog name from `pack-builder` (active row’s name input), for cart line `properties.dog`.
   * @returns {string} trimmed name, or '' if unavailable
   */
  #getPackBuilderSelectedDogName() {
    const { nameInput } = this.#getPackBuilderActiveRowInputs();
    if (nameInput) return String(nameInput.value ?? '').trim().slice(0, 250);
    return '';
  }

  /**
   * Weight (lbs) from the same pack row as `#getPackBuilderSelectedDogName`.
   * @returns {number | null} positive finite number, or null if missing/invalid
   */
  #getPackBuilderSelectedDogWeight() {
    const { weightInput } = this.#getPackBuilderActiveRowInputs();
    if (!weightInput) return null;
    const v = parseFloat(String(weightInput.value ?? '').trim());
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  /**
   * @param {boolean} missingName
   * @param {boolean} missingWeight
   */
  #updatePackBuilderEmptyFieldHints(missingName, missingWeight) {
    const pack = this.#findPackBuilderForProductForm();
    if (!pack) return;
    const nameHint = pack.querySelector('.js-doges-name-empty');
    const weightHint = pack.querySelector('.js-doges-weight-empty');
    
    if (nameHint instanceof HTMLElement) {
      if (missingName || missingWeight) {
        nameHint.removeAttribute('hidden');
        nameHint.textContent = missingName && missingWeight
          ? "ADD YOUR DOG’S NAME & WEIGHT."
          : missingName
            ? "ADD YOUR DOG’S NAME & WEIGHT."
            : "ADD YOUR DOG’S NAME & WEIGHT.";
      } else {
        nameHint.setAttribute('hidden', '');
      }
    }
    
    if (weightHint instanceof HTMLElement) {
      weightHint.setAttribute('hidden', ''); // We hide this since we're using nameHint for both
    }
  }

  #clearPackBuilderEmptyFieldHints() {
    this.#updatePackBuilderEmptyFieldHints(false, false);
  }

  /**
   * Offset for scroll targets so content clears sticky header (`utilities.js` / `header.js` set these on `body`).
   * @returns {number} pixels
   */
  #getStickyHeaderScrollOffsetPx() {
    const parsePx = (el, prop) => {
      if (!(el instanceof Element)) return 0;
      const raw = getComputedStyle(el).getPropertyValue(prop).trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };
    const body = document.body;
    const root = document.documentElement;
    const group = parsePx(body, '--header-group-height') || parsePx(root, '--header-group-height');
    const header = parsePx(body, '--header-height') || parsePx(root, '--header-height');
    const base = group > 0 ? group : header;
    const withGap = base + 12;
    return withGap > 12 ? withGap : 84;
  }

  /** Scrolls pack-builder into view below sticky header chrome. */
  #scrollPackBuilderErrorsIntoView() {
    const pack = this.#findPackBuilderForProductForm();
    if (!pack) return;
    requestAnimationFrame(() => {
      const offset = this.#getStickyHeaderScrollOffsetPx();
      const y = pack.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    });
  }

  /**
   * When `pack-builder` is present on the PDP, require name and weight for every dog before add-to-cart.
   * @returns {boolean} true if the add must be aborted
   */
  #blockAddIfPackFieldsIncomplete() {
    const pack = this.#findPackBuilderForProductForm();
    if (!pack) return false;

    const rows = pack.querySelectorAll('.artie-pack-builder__dog-row');
    if (!rows.length) {
      this.#updatePackBuilderEmptyFieldHints(true, true);
      this.#scrollPackBuilderErrorsIntoView();
      return true;
    }

    let missingName = false;
    let missingWeight = false;
    /** @type {HTMLInputElement | null} */
    let firstNameInput = null;
    /** @type {HTMLInputElement | null} */
    let firstWeightInput = null;

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;
      const nameEl =
        row.querySelector('.artie-pack-builder__input-name') || row.querySelector('.js-dog-name');
      const weightEl = row.querySelector('.artie-pack-builder__input-weight');
      const name = nameEl instanceof HTMLInputElement ? String(nameEl.value ?? '').trim() : '';
      const weight =
        weightEl instanceof HTMLInputElement ? parseFloat(String(weightEl.value ?? '').trim()) : NaN;
      const nameBad = !name;
      const weightBad = !Number.isFinite(weight) || weight <= 0;
      if (nameBad) {
        missingName = true;
        if (!firstNameInput && nameEl instanceof HTMLInputElement) firstNameInput = nameEl;
      }
      if (weightBad) {
        missingWeight = true;
        if (!firstWeightInput && weightEl instanceof HTMLInputElement) firstWeightInput = weightEl;
      }
    }

    if (!missingName && !missingWeight) {
      this.#clearPackBuilderEmptyFieldHints();
      return false;
    }

    this.#updatePackBuilderEmptyFieldHints(missingName, missingWeight);
    this.#scrollPackBuilderErrorsIntoView();
    const msg =
      missingName && missingWeight
        ? "Please enter each dog's name and weight."
        : missingName
          ? "Please enter each dog's name."
          : "Please enter each dog's weight.";
    this.#setLiveRegionText(msg);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
    if (missingName && firstNameInput) firstNameInput.focus();
    else if (missingWeight && firstWeightInput) firstWeightInput.focus();
    return true;
  }

  /**
   * Pack name/weight gate — used by `add-to-cart-component` and sticky/sync puppet before fly animation.
   * @returns {boolean} true when add must not proceed (inline hints + live region)
   */
  artieShouldBlockAddForPackFields() {
    return this.#blockAddIfPackFieldsIncomplete();
  }

  /**
   * Same sources as `getArtieCustomerId` in `artie-calc.js`, plus `artie_pets_store.customer_id`.
   * @returns {string}
   */
  #getArtieCustomerIdForLineItem() {
    const el = document.getElementById('artieCustomerId');
    if (el instanceof HTMLInputElement && el.value) return String(el.value).trim();
    const attr = document.documentElement.getAttribute('data-artie-customer-id');
    if (attr) return String(attr).trim();
    try {
      const raw = localStorage.getItem('artie_pets_store');
      if (raw) {
        const parsed = /** @type {{ customer_id?: string | number } | null} */ (JSON.parse(raw));
        const cid = parsed?.customer_id;
        if (cid != null && String(cid).trim() !== '') return String(cid).trim();
      }
    } catch {
      /* ignore */
    }
    return '';
  }

  /**
   * Artie pet key for cart `properties._dog_id`: `pet_{customerId}_{packRowId}` (guest when not logged in),
   * matching `artie-calc.js` `saveQuizToArtiePetsStore` / `artie_pets_store` pet ids.
   * @returns {string} e.g. `pet_9333375107244_1777896168797`, or '' if no pack row id
   */
  #getPackBuilderSelectedDogId() {
    const { row } = this.#getPackBuilderActiveRowInputs();
    const packRowId = row?.dataset?.id != null && String(row.dataset.id).trim() !== '' ? String(row.dataset.id).trim() : '';
    if (!packRowId) return '';
    const customerSegment = this.#getArtieCustomerIdForLineItem() || 'guest';
    return `pet_${customerSegment}_${packRowId}`.slice(0, 250);
  }

  /**
   * Reads meal-plan commerce ids from `artie-calc.js` global API.
   * @returns {{ variantId: string, sellingPlanId: string, perMealPrice: string } | null}
   */
  #getArtieMealPlanCommerceFromWindow() {
    try {
      const globalApi = /** @type {{ getArtieMealPlanCommerceIds?: () => { variant_id?: number | string | null, selling_plan_id?: number | string | null, permeal_price?: string | null } }} */ (window);
      if (typeof globalApi.getArtieMealPlanCommerceIds !== 'function') return null;
      const ids = globalApi.getArtieMealPlanCommerceIds();
      const variantId = ids?.variant_id != null ? String(ids.variant_id).trim() : '';
      const sellingPlanId = ids?.selling_plan_id != null ? String(ids.selling_plan_id).trim() : '';
      const perMealPrice = ids?.permeal_price != null ? String(ids.permeal_price).trim() : '';
      if (!variantId && !sellingPlanId && !perMealPrice) return null;
      return { variantId, sellingPlanId, perMealPrice };
    } catch {
      return null;
    }
  }

  /**
   * @returns {boolean}
   */
  #shouldUseMultipleDogAtc() {
    return !!this.#findPackBuilderForProductForm();
  }

  /**
   * Rows from `localStorage.multiple-dog-atc` (kept in sync by `artie-calc.js`).
   * @returns {Array<Record<string, string>>}
   */
  #getMultipleDogAtcEntries() {
    try {
      const globalApi = /** @type {{ getArtieMultipleDogAtc?: () => Array<Record<string, string>> }} */ (window);
      if (typeof globalApi.getArtieMultipleDogAtc === 'function') {
        const rows = globalApi.getArtieMultipleDogAtc();
        return Array.isArray(rows) ? rows : [];
      }
      const raw = localStorage.getItem('multiple-dog-atc');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @typedef {{ variantId: string, key: string }} ArtieCartMealLineRef
   */

  /**
   * @param {string[]} dogIds
   * @param {string[]} dogNames
   * @returns {boolean}
   */
  #cartLineMatchesDogIdentifiers(dogIds, dogNames, lineDogId, lineDogName) {
    const dogIdSet = new Set(dogIds.filter(Boolean).map((id) => String(id).trim()).filter(Boolean));
    const dogNameSet = new Set(
      dogNames
        .filter(Boolean)
        .map((name) => String(name).trim().toLowerCase())
        .filter(Boolean)
    );
    const id = String(lineDogId || '').trim();
    const name = String(lineDogName || '').trim().toLowerCase();
    if (id && dogIdSet.has(id)) return true;
    if (name && dogNameSet.has(name)) return true;
    return false;
  }

  /**
   * All main meal line refs for one dog from cart JSON (supports duplicate lines per dog).
   * @param {string} dogId
   * @param {string} [dogName]
   * @param {{ items?: unknown[] } | null | undefined} [cart]
   * @returns {ArtieCartMealLineRef[]}
   */
  #getArtieMainMealLineRefsForDogFromCart(dogId, dogName, cart) {
    /** @type {ArtieCartMealLineRef[]} */
    const lines = [];
    if (!cart?.items?.length) return lines;

    const dogIds = dogId ? [dogId] : [];
    const dogNames = dogName ? [dogName] : [];

    for (const raw of cart.items) {
      const item = /** @type {{ key?: string, properties?: Record<string, string>, variant_id?: number }} */ (raw);
      if (!this.#isArtieMainMealCartItem(item)) continue;

      const itemDogId = this.#getCartLinePropertyValue(item.properties, '_dog_id');
      const itemDogName = this.#getCartLinePropertyValue(item.properties, 'dog');
      if (!this.#cartLineMatchesDogIdentifiers(dogIds, dogNames, itemDogId, itemDogName)) continue;

      const key = item.key?.trim();
      if (!key) continue;
      lines.push({
        key,
        variantId: String(item.variant_id ?? '').trim(),
      });
    }

    return lines;
  }

  /**
   * @param {string} dogId
   * @param {string} [dogName]
   * @returns {ArtieCartMealLineRef[]}
   */
  #getArtieMainMealLineRefsForDogFromDom(dogId, dogName) {
    const dogIds = dogId ? [dogId] : [];
    const dogNames = dogName ? [dogName] : [];
    /** @type {ArtieCartMealLineRef[]} */
    const lines = [];

    for (const row of document.querySelectorAll(
      'cart-items-component tr.cart-items__table-row[data-key]'
    )) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      if (!this.#isArtieMainMealCartDomRow(row)) continue;

      const rowDogId = row.dataset.dogId?.trim() ?? '';
      const rowDogName = row.dataset.dogName?.trim() ?? '';
      if (!this.#cartLineMatchesDogIdentifiers(dogIds, dogNames, rowDogId, rowDogName)) continue;

      const key = row.dataset.key?.trim();
      if (!key) continue;
      lines.push({
        key,
        variantId: row.dataset.variantId?.trim() ?? '',
      });
    }

    return lines;
  }

  /**
   * Single pass over cart JSON — finds every main meal line for the given dogs (including duplicates).
   * @param {{ items?: unknown[] }} cart
   * @param {string[]} dogIds
   * @param {string[]} dogNames
   * @returns {ArtieCartMealLineRef[]}
   */
  #collectArtieMainMealLinesFromCart(cart, dogIds, dogNames) {
    /** @type {ArtieCartMealLineRef[]} */
    const lines = [];
    if (!cart?.items?.length) return lines;

    const seenKeys = new Set();

    for (const raw of cart.items) {
      const item = /** @type {{ key?: string, properties?: Record<string, string>, variant_id?: number }} */ (raw);
      if (!this.#isArtieMainMealCartItem(item)) continue;

      const itemDogId = this.#getCartLinePropertyValue(item.properties, '_dog_id');
      const itemDogName = this.#getCartLinePropertyValue(item.properties, 'dog');
      if (!this.#cartLineMatchesDogIdentifiers(dogIds, dogNames, itemDogId, itemDogName)) continue;

      const key = item.key?.trim();
      if (!key || seenKeys.has(key)) continue;
      seenKeys.add(key);
      lines.push({
        key,
        variantId: String(item.variant_id ?? '').trim(),
      });
    }

    return lines;
  }

  /**
   * Union of main meal lines for these dogs. Cart JSON is authoritative when available.
   * @param {string[]} dogIds
   * @param {string[]} dogNames
   * @param {{ items?: unknown[] } | null | undefined} [cartPreload]
   * @returns {ArtieCartMealLineRef[]}
   */
  #getAllArtieMainMealLineRefsForDogIdentifiers(dogIds, dogNames, cartPreload) {
    if (cartPreload?.items?.length) {
      return this.#collectArtieMainMealLinesFromCart(cartPreload, dogIds, dogNames);
    }

    const seenKeys = new Set();
    /** @type {ArtieCartMealLineRef[]} */
    const lines = [];

    const addLine = (line) => {
      if (!line?.key || seenKeys.has(line.key)) return;
      seenKeys.add(line.key);
      lines.push(line);
    };

    for (const dogId of dogIds) {
      for (const line of this.#getArtieMainMealLineRefsForDogFromDom(dogId, '')) {
        addLine(line);
      }
    }
    for (const dogName of dogNames) {
      for (const line of this.#getArtieMainMealLineRefsForDogFromDom('', dogName)) {
        addLine(line);
      }
    }

    return lines;
  }

  /**
   * True when the dog has no meal line, duplicate meal lines, or no line with the desired variant.
   * @param {Record<string, string>} entry
   * @param {{ items?: unknown[] } | null | undefined} [cartPreload]
   * @returns {boolean}
   */
  #entryNeedsCartMealUpdate(entry, cartPreload) {
    const variantId = entry?.variantId != null ? String(entry.variantId).trim() : '';
    if (!variantId) return false;

    const dogId = entry?.dogId != null ? String(entry.dogId).trim() : '';
    const dogName = entry?.dogName != null ? String(entry.dogName).trim() : '';
    if (!dogId && !dogName) return true;

    const lines = this.#getAllArtieMainMealLineRefsForDogIdentifiers(
      dogId ? [dogId] : [],
      !dogId && dogName ? [dogName] : [],
      cartPreload
    );

    if (!lines.length) return true;
    if (lines.length > 1) return true;

    return lines[0].variantId !== variantId;
  }

  /**
   * Queue only dogs whose meal variant differs from the cart (or are not in the cart yet).
   * Empty cart → all resolvable pack dogs (initial multi-dog add).
   * @param {Array<Record<string, string>>} entries
   * @param {{ items?: unknown[] } | null | undefined} [cartPreload]
   * @returns {Array<Record<string, string>>}
   */
  #filterMultipleDogAtcEntriesForAdd(entries, cartPreload) {
    if (!entries.length) return [];

    const resolvable = entries.filter((entry) => {
      const variantId = entry?.variantId != null ? String(entry.variantId).trim() : '';
      return !!variantId;
    });
    if (!resolvable.length) return [];

    const hasAnyCartMeals = resolvable.some((entry) => {
      const dogId = entry?.dogId != null ? String(entry.dogId).trim() : '';
      const dogName = entry?.dogName != null ? String(entry.dogName).trim() : '';
      return (
        this.#getAllArtieMainMealLineRefsForDogIdentifiers(
          dogId ? [dogId] : [],
          !dogId && dogName ? [dogName] : [],
          cartPreload
        ).length > 0
      );
    });

    if (!hasAnyCartMeals) return resolvable;

    return resolvable.filter((entry) => this.#entryNeedsCartMealUpdate(entry, cartPreload));
  }

  /**
   * Shared `/cart.js` fetch (deduped in-flight). Use `#resolveCartSnapshotForMealAtc` when possible.
   * @param {boolean} [forceRefresh]
   * @returns {Promise<{ items?: unknown[], item_count?: number } | null>}
   */
  async #fetchCartForMealSync(forceRefresh = false) {
    if (!forceRefresh && this.#mealCartSnapshot) {
      return this.#mealCartSnapshot;
    }

    if (!forceRefresh && this.#mealCartFetchPromise) {
      return this.#mealCartFetchPromise;
    }

    this.#mealCartFetchPromise = (async () => {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.#mealCartSnapshot = cart;
        return cart;
      } catch {
        return null;
      } finally {
        this.#mealCartFetchPromise = null;
      }
    })();

    return this.#mealCartFetchPromise;
  }

  /**
   * @param {Record<string, string | Array<{ name?: string, value?: string }>> | null | undefined} properties
   * @param {string} key
   * @returns {string}
   */
  #getCartLinePropertyValue(properties, key) {
    const p = properties;
    if (p == null) return '';
    if (Array.isArray(p)) {
      for (const row of p) {
        if (row && String(row.name) === key) return String(row.value ?? '').trim();
      }
      return '';
    }
    if (typeof p === 'object' && p[key] != null) return String(p[key]).trim();
    return '';
  }

  /**
   * @param {{ properties?: Record<string, string | Array<{ name?: string, value?: string }>>, variant_id?: number }} item
   * @returns {boolean}
   */
  #isArtieMainMealCartItem(item) {
    const role = this.#getCartLinePropertyValue(item.properties, '_artie_line');
    if (role === 'system' || role === 'secondary') return false;
    if (role === 'main') return true;
    if (this.#getCartLinePropertyValue(item.properties, 'dog')) return true;
    const syncMain = this.#getCartLinePropertyValue(item.properties, '_artie_sync_main');
    const vid = String(item.variant_id ?? '').trim();
    if (syncMain && vid && syncMain === vid) return true;
    return false;
  }

  /**
   * @param {HTMLTableRowElement} row
   * @returns {boolean}
   */
  #isArtieMainMealCartDomRow(row) {
    const role = row.getAttribute('data-artie-line')?.trim() ?? '';
    if (role === 'system' || role === 'secondary') return false;
    if (role === 'main') return true;
    if (row.dataset.dogId?.trim()) return true;
    if (row.dataset.dogName?.trim()) return true;
    return row.closest('.cart-items__tbody--regular') != null && role !== 'system' && role !== 'secondary';
  }

  /**
   * Removes existing main meal lines for the given dogs before a replacement add.
   * Uses cart JSON (all line keys per `_dog_id`) plus DOM rows; clears every duplicate main line.
   * @param {{ dogIds?: string[], dogNames?: string[] }} identifiers
   * @param {string} sections
   * @param {{ items?: unknown[] } | null} [preloadedCart]
   */
  async #removeArtieMainMealsForDogIdentifiers(identifiers, sections, preloadedCart = null) {
    if (
      typeof Theme === 'undefined' ||
      (!Theme.routes?.cart_update_url && !Theme.routes?.cart_change_url)
    ) {
      return;
    }

    const dogIds = identifiers.dogIds ?? [];
    const dogNames = identifiers.dogNames ?? [];
    if (!dogIds.length && !dogNames.length) return;

    let cart = preloadedCart;
    if (!cart && this.#isDomCartSnapshotAuthoritative()) {
      cart = this.#buildCartSnapshotFromDom();
    }

    let lines = this.#getAllArtieMainMealLineRefsForDogIdentifiers(dogIds, dogNames, cart);
    let keys = lines.map((line) => line.key).filter(Boolean);

    if (!keys.length && this.#getThemeCartItemCount() !== 0) {
      cart = await this.#fetchCartForMealSync(true);
      lines = this.#getAllArtieMainMealLineRefsForDogIdentifiers(dogIds, dogNames, cart);
      keys = lines.map((line) => line.key).filter(Boolean);
    }

    if (!keys.length) return;

    await this.#clearCartLinesByKeys(keys, sections, false);
    this.#invalidateMealCartSnapshot();
  }

  /**
   * One main cart line per pack dog from `multiple-dog-atc`, plus optional system/secondary sync rows.
   * @param {number} mainQuantity
   * @param {{ items?: unknown[] } | null | undefined} [cartPreload]
   * @returns {ArtieCartQueueLine[] | null}
   */
  #buildCartLinesFromMultipleDogAtc(mainQuantity, cartPreload) {
    const entries = this.#filterMultipleDogAtcEntriesForAdd(this.#getMultipleDogAtcEntries(), cartPreload);
    if (!entries.length) return null;

    const mainQty =
      typeof mainQuantity === 'number' && Number.isFinite(mainQuantity) && mainQuantity > 0
        ? Math.floor(mainQuantity)
        : Number(this.dataset.quantityDefault) || 1;

    /** @type {ArtieCartQueueLine[]} */
    const lines = [];

    const activeDogId = this.#getPackBuilderSelectedDogId();
    const pricePropsApi = /** @type {{ getArtieCartLinePricePropertiesForPackEntry?: (entry: Record<string, string>, isActiveDog?: boolean) => { permeal_price?: string } }} */ (
      window
    );

    for (const entry of entries) {
      const variantId = entry?.variantId != null ? String(entry.variantId).trim() : '';
      if (!variantId) continue;

      const dogName = entry?.dogName != null ? String(entry.dogName).trim().slice(0, 250) : '';
      const dogId = entry?.dogId != null ? String(entry.dogId).trim().slice(0, 250) : '';
      const sellingPlanId =
        entry?.sellingPlanId != null ? String(entry.sellingPlanId).trim() : '';
      /** @type {Record<string, string>} */
      const properties = {
        _artie_sync_main: variantId,
        _artie_line: 'main',
      };
      if (dogName) properties.dog = dogName;
      if (dogId) properties._dog_id = dogId;

      const isActiveDog = !!(activeDogId && dogId && activeDogId === dogId);
      const priceProps =
        typeof pricePropsApi.getArtieCartLinePricePropertiesForPackEntry === 'function'
          ? pricePropsApi.getArtieCartLinePricePropertiesForPackEntry(entry, isActiveDog)
          : null;
      const perMealPrice =
        priceProps?.permeal_price != null ? String(priceProps.permeal_price).trim().slice(0, 250) : '';
      if (perMealPrice) properties.permeal_price = perMealPrice;

      /** @type {ArtieCartQueueLine} */
      const mainLine = {
        variantId,
        quantity: mainQty,
        properties,
        artieLineRole: 'main',
      };
      if (sellingPlanId) mainLine.sellingPlanId = sellingPlanId;
      lines.push(mainLine);
    }

    if (!lines.length) return null;

    const syncExtras = this.#getArtieSyncLineItems(lines[0].variantId, 1);
    if (syncExtras && syncExtras.length > 1) {
      for (let i = 1; i < syncExtras.length; i++) {
        const addon = syncExtras[i];
        if (addon) lines.push(addon);
      }
    }

    return lines;
  }

  /**
   * @returns {Promise<void>}
   */
  async #syncMultipleDogAtcBeforeAdd() {
    try {
      const globalApi = /** @type {{ syncArtieMultipleDogAtcAsync?: () => Promise<void> }} */ (window);
      if (typeof globalApi.syncArtieMultipleDogAtcAsync === 'function') {
        await globalApi.syncArtieMultipleDogAtcAsync();
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Line-item properties used to link bundle rows: `_artie_sync_main` = PDP variant id, `_artie_line` = role.
   * Main line also includes `dog` (selected pack-builder dog name) and `_dog_id` when present.
   * @param {string} mainVariantId
   * @param {'main' | 'system' | 'secondary'} role
   * @param {string} [perMealPrice]
   * @returns {Record<string, string>}
   */
  #artieSyncLineProperties(mainVariantId, role, perMealPrice = '') {
    const id = String(mainVariantId);
    /** @type {Record<string, string>} */
    const props = {
      _artie_sync_main: id,
      _artie_line: role,
    };
    if (role === 'main') {
      const dog = this.#getPackBuilderSelectedDogName();
      if (dog) props.dog = dog;
      const dogId = this.#getPackBuilderSelectedDogId();
      if (dogId) props._dog_id = dogId;
      if (perMealPrice) props.permeal_price = perMealPrice;
    }
    return props;
  }

  /**
   * Whether the theme has rendered any cart row for these Artie roles (cart drawer / cart page).
   * @param {('system' | 'secondary')[]} roles
   * @returns {boolean}
   */
  #hasArtieAddonDomRowsForRoles(roles) {
    const roleSet = new Set(roles);
    for (const row of document.querySelectorAll(
      'cart-items-component tr.cart-items__table-row[data-artie-line]'
    )) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      const role = row.getAttribute('data-artie-line');
      if (role === 'system' || role === 'secondary') {
        if (roleSet.has(role)) return true;
      }
    }
    return false;
  }

  /**
   * Line item keys for Artie add-on rows to remove: `roles` minus rows whose variant matches `keepVariantByRole[role]`.
   * @param {('system' | 'secondary')[]} roles
   * @param {{ system?: string, secondary?: string }} [keepVariantByRole] variant ids from this batch — same id is not removed
   * @returns {string[]}
   */
  #getArtieAddonLineKeysFromDom(roles, keepVariantByRole = {}) {
    const roleSet = new Set(roles);
    const keepSystem =
      keepVariantByRole.system != null && keepVariantByRole.system !== ''
        ? String(keepVariantByRole.system)
        : null;
    const keepSecondary =
      keepVariantByRole.secondary != null && keepVariantByRole.secondary !== ''
        ? String(keepVariantByRole.secondary)
        : null;
    const seen = new Set();
    /** @type {string[]} */
    const keys = [];

    for (const row of document.querySelectorAll(
      'cart-items-component tr.cart-items__table-row[data-artie-line]'
    )) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      const role = row.getAttribute('data-artie-line');
      if (role !== 'system' && role !== 'secondary') continue;
      if (!roleSet.has(role)) continue;
      const variantId = row.dataset.variantId?.trim() ?? '';
      if (role === 'system' && keepSystem !== null && variantId === keepSystem) continue;
      if (role === 'secondary' && keepSecondary !== null && variantId === keepSecondary) continue;
      const key = row.dataset.key?.trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
    return keys;
  }

  /**
   * Cart already shows this Artie add-on variant for `role` (same id — no add/remove needed for that line).
   * @param {'system' | 'secondary'} role
   * @param {string} variantId
   * @returns {boolean}
   */
  #isArtieAddonVariantInCartDom(role, variantId) {
    const vid = String(variantId);
    for (const row of document.querySelectorAll(
      'cart-items-component tr.cart-items__table-row[data-artie-line]'
    )) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      if (row.getAttribute('data-artie-line') !== role) continue;
      if ((row.dataset.variantId?.trim() ?? '') === vid) return true;
    }
    return false;
  }

  /**
   * @param {string[]} keys
   * @param {string} sections
   * @param {boolean} [includeSections]
   */
  async #clearCartLinesByKeysSequential(keys, sections, includeSections = true) {
    if (!Theme.routes?.cart_change_url) return;
    for (const key of keys) {
      if (!key) continue;
      const payload =
        includeSections && sections
          ? {
              id: key,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }
          : { id: key, quantity: 0 };
      await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(payload) }));
    }
  }

  /**
   * Zero-quantity remove by line key. Uses one `/cart/update.js` when possible (avoids parallel `/cart/change` races).
   * @param {string[]} keys
   * @param {string} sections
   * @param {boolean} [includeSections] - false for Artie purge steps: smaller responses; final add still sends sections.
   */
  async #clearCartLinesByKeys(keys, sections, includeSections = true) {
    const uniqueKeys = [...new Set(keys.filter(Boolean).map((k) => String(k).trim()).filter(Boolean))];
    if (!uniqueKeys.length) return;

    if (Theme.routes?.cart_update_url) {
      /** @type {Record<string, number>} */
      const updates = {};
      for (const key of uniqueKeys) {
        updates[key] = 0;
      }

      /** @type {Record<string, unknown>} */
      const payload = { updates };
      if (includeSections && sections) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', { body: JSON.stringify(payload) })
        );
        if (response.ok) return;
      } catch {
        /* fall through to sequential change */
      }
    }

    await this.#clearCartLinesByKeysSequential(uniqueKeys, sections, includeSections);
  }

  /**
   * Removes Artie add-on lines for `roles`, except rows matching `keepVariantByRole` (same variant id as this batch).
   * Uses cart row markup first; if the cart UI is present but nothing needs removing, skips `/cart.js`.
   * @param {('system' | 'secondary')[]} roles
   * @param {string} sections
   * @param {{ system?: string, secondary?: string }} [keepVariantByRole]
   * @param {{ items?: unknown[] } | null} [preloadedCart] - when set, avoids a duplicate `/cart.js` (e.g. batch pre-fetch).
   */
  async #removeAllArtieLinesForRoles(roles, sections, keepVariantByRole = {}, preloadedCart = null) {
    if (typeof Theme === 'undefined' || !Theme.routes?.cart_change_url || !roles?.length) return;

    const domKeys = this.#getArtieAddonLineKeysFromDom(roles, keepVariantByRole);
    const cartRenderedForRoles = this.#hasArtieAddonDomRowsForRoles(roles);

    if (domKeys.length > 0) {
      await this.#clearCartLinesByKeys(domKeys, sections, false);
      return;
    }
    if (cartRenderedForRoles) {
      return;
    }

    /** @type {{ items?: unknown[] }} */
    let cart;
    if (preloadedCart != null && typeof preloadedCart === 'object') {
      cart = preloadedCart;
    } else {
      const cartRes = await fetch('/cart.js');
      cart = await cartRes.json();
    }
    if (!cart.items?.length) return;

    const roleSet = new Set(roles);
    const keepSystem =
      keepVariantByRole.system != null && keepVariantByRole.system !== ''
        ? String(keepVariantByRole.system)
        : null;
    const keepSecondary =
      keepVariantByRole.secondary != null && keepVariantByRole.secondary !== ''
        ? String(keepVariantByRole.secondary)
        : null;

    const targets = cart.items.filter((/** @type {{ key?: string, variant_id?: number, properties?: Record<string, string> }} */ item) => {
      const p = item.properties || {};
      const lineRole = String(p._artie_line || '');
      if (lineRole !== 'system' && lineRole !== 'secondary') return false;
      if (!roleSet.has(lineRole)) return false;
      const vid = String(item.variant_id ?? '');
      if (lineRole === 'system' && keepSystem !== null && vid === keepSystem) return false;
      if (lineRole === 'secondary' && keepSecondary !== null && vid === keepSecondary) return false;
      return true;
    });

    /** @type {string[]} */
    const keys = [];
    const seen = new Set();
    for (const raw of targets) {
      const item = /** @type {{ key?: string, variant_id?: number, properties?: Record<string, string> }} */ (raw);
      const key = item.key;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
    await this.#clearCartLinesByKeys(keys, sections, false);
  }

  /**
   * When the Product System Card enables sync (`add_to_cart_sync_product`), returns the line items for JSON `/cart/add.js`:
   * main first (PDP qty), then optionally system (qty 1) and/or secondary (qty 1) — each addon is included only when its
   * variant id resolves. Each row carries `_artie_sync_main` + `_artie_line` properties.
   *
   * Returns `null` (→ standard FormData add) when neither addon is configured, so the main product still adds normally.
   *
   * @param {string} mainVariantId
   * @param {number} mainQuantity from `#getQuantity()` (or queue override)
   * @returns {ArtieCartQueueLine[] | null}
   */
  #getArtieSyncLineItems(mainVariantId, mainQuantity) {
    if (!mainVariantId) return null;
    const card = this.#getArtieProductSystemSyncCard();
    if (!card) return null;

    const secondaryRaw = card.getAttribute('js-data-secondary-product');
    const parsedSecondary = secondaryRaw ? Number(secondaryRaw) : NaN;
    const secondaryId =
      Number.isFinite(parsedSecondary) && parsedSecondary > 0 ? parsedSecondary : null;

    const checkedSwatch =
      card.querySelector('input.product-system-card__swatch-input:checked') ||
      card.querySelector('input.product-system-card__swatch-input[checked]') ||
      card.querySelector('input.product-system-card__swatch-input');
    let parsedSystem = NaN;
    if (checkedSwatch instanceof HTMLInputElement) {
      parsedSystem = Number(checkedSwatch.getAttribute('js-system-card-variant-id') || checkedSwatch.value);
    } else if (card instanceof HTMLElement && card.dataset.systemCardVariantId) {
      parsedSystem = Number(card.dataset.systemCardVariantId);
    }
    const systemId = Number.isFinite(parsedSystem) && parsedSystem > 0 ? parsedSystem : null;

    // No addons resolved → let the standard FormData path add the main product on its own
    // (so PDP-only properties like `dog` / `_dog_id` / `permeal_price` are still applied).
    if (systemId === null && secondaryId === null) return null;

    const mainQty =
      typeof mainQuantity === 'number' && Number.isFinite(mainQuantity) && mainQuantity > 0
        ? Math.floor(mainQuantity)
        : Number(this.dataset.quantityDefault) || 1;

    const commerce = this.#getArtieMealPlanCommerceFromWindow();
    const syncMain = commerce?.variantId ? String(commerce.variantId) : String(mainVariantId);

    /** @type {ArtieCartQueueLine} */
    const mainLine = {
      variantId: syncMain,
      quantity: mainQty,
      properties: this.#artieSyncLineProperties(syncMain, 'main', commerce?.perMealPrice || ''),
      artieLineRole: 'main',
    };
    if (commerce?.sellingPlanId) {
      mainLine.sellingPlanId = commerce.sellingPlanId;
    }

    /** @type {ArtieCartQueueLine[]} */
    const lines = [mainLine];

    if (systemId !== null) {
      lines.push({
        variantId: String(systemId),
        quantity: 1,
        properties: this.#artieSyncLineProperties(syncMain, 'system'),
        artieLineRole: 'system',
      });
    }

    if (secondaryId !== null) {
      lines.push({
        variantId: String(secondaryId),
        quantity: 1,
        properties: this.#artieSyncLineProperties(syncMain, 'secondary'),
        artieLineRole: 'secondary',
      });
    }

    return lines;
  }

  /**
   * Sticky bar sets this on the form before a puppet add so cart UI waits for fly + button motion.
   * @returns {Promise<void> | undefined}
   */
  #takeStickyCartPresentationPromise() {
    const el = /** @type {HTMLElement & { __stickyCartPresentationForNextAdd?: Promise<void> }} */ (this);
    const p = el.__stickyCartPresentationForNextAdd;
    if (p) delete el.__stickyCartPresentationForNextAdd;
    return p;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    if (!overrideVariantId && this.#blockAddIfPackFieldsIncomplete()) return;

    const mainVariantId = (overrideVariantId || this.refs.variantId?.value)?.toString();
    const mainQuantity = overrideQuantity !== undefined ? overrideQuantity : this.#getQuantity();

    if (!overrideVariantId && this.#shouldUseMultipleDogAtc()) {
      const atcEntries = this.#getMultipleDogAtcEntries();
      const [, cartPreload] = await Promise.all([
        this.#syncMultipleDogAtcBeforeAdd(),
        this.#resolveCartSnapshotForMealAtc(atcEntries),
      ]);
      const multiDogLines = this.#buildCartLinesFromMultipleDogAtc(mainQuantity, cartPreload);
      if (multiDogLines && multiDogLines.length > 0) {
        this.#processBatchAddToCart(multiDogLines, event, cartPreload);
        return;
      }
      if (atcEntries.length) return;
    }

    const artieSyncLines =
      !overrideVariantId && mainVariantId ? this.#getArtieSyncLineItems(mainVariantId, mainQuantity) : null;

    if (artieSyncLines && artieSyncLines.length > 1) {
      this.#processBatchAddToCart(artieSyncLines, event);
      return;
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    if (!overrideVariantId) {
      if (this.#findPackBuilderForProductForm()) {
        formData.set('properties[_artie_line]', 'main');
      }
      const dogName = this.#getPackBuilderSelectedDogName();
      if (dogName) formData.set('properties[dog]', dogName);
      const dogId = this.#getPackBuilderSelectedDogId();
      if (dogId) formData.set('properties[_dog_id]', dogId);
      const commerce = this.#getArtieMealPlanCommerceFromWindow();
      if (commerce?.perMealPrice) formData.set('properties[permeal_price]', commerce.perMealPrice);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const stickyCartPresentation = this.#takeStickyCartPresentationPromise();

    const dogIdForReplace = this.#getPackBuilderSelectedDogId();
    const dogNameForReplace = this.#getPackBuilderSelectedDogName();
    const sectionsForReplace = cartItemComponentsSectionIds.join(',');

    const runSingleAdd = async () => {
      const commerceForCompare = this.#getArtieMealPlanCommerceFromWindow();
      const compareVariantId =
        commerceForCompare?.variantId || (mainVariantId ? String(mainVariantId) : '');
      const singleEntry = dogIdForReplace
        ? [{ dogId: dogIdForReplace, variantId: compareVariantId }]
        : [];
      const cartPreloadForSingle = await this.#resolveCartSnapshotForMealAtc(singleEntry);

      if (dogIdForReplace && compareVariantId) {
        const needsUpdate = this.#entryNeedsCartMealUpdate(
          { dogId: dogIdForReplace, variantId: compareVariantId },
          cartPreloadForSingle
        );
        if (!needsUpdate) return null;
      }

      if (dogIdForReplace || dogNameForReplace) {
        await this.#removeArtieMainMealsForDogIdentifiers(
          {
            dogIds: dogIdForReplace ? [dogIdForReplace] : [],
            dogNames: !dogIdForReplace && dogNameForReplace ? [dogNameForReplace] : [],
          },
          sectionsForReplace,
          cartPreloadForSingle
        );
      }

      return fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
    };

    runSingleAdd()
      .then((response) => (response ? response.json() : null))
      .then(async (addResponse) => {
        if (!addResponse) return;

        if (stickyCartPresentation) {
          try {
            await stickyCartPresentation;
          } catch {
            // Ignore animation failures; cart state should still update.
          }
        }

        if (addResponse.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', addResponse.message, addResponse.description, addResponse.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = addResponse.message;
          } else {
            const newTextNode = document.createTextNode(addResponse.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(addResponse.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          const cartResource =
            addResponse && typeof addResponse === 'object' && Array.isArray(addResponse.items) ? addResponse : undefined;
          if (cartResource) {
            this.#updateCartQuantityFromData(cartResource);
          } else {
            void this.#fetchAndUpdateCartQuantity();
          }

          const defaultQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
          const itemCountForEvent =
            addResponse && typeof addResponse === 'object' && typeof addResponse.item_count === 'number'
              ? addResponse.item_count
              : defaultQty;

          this.dispatchEvent(
            new CartAddEvent(cartResource ?? addResponse, id.toString(), {
              source: 'product-form-component',
              itemCount: itemCountForEvent,
              productId: this.dataset.productId,
              sections: addResponse.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Artie sync: one `/cart/add.js` with all lines in `items` (main + system + secondary), plus a single `/cart.js`
   * before purge when needed (empty cart skips purge entirely). `CartAddEvent` uses that add response so the drawer
   * morphs without per-line round trips.
   *
   * @param {ArtieCartQueueLine[]} items
   * @param {Event} [event] original user action for performance telemetry
   * @param {{ items?: unknown[], item_count?: number } | null} [cartSnapshotFromCaller]
   */
  #processBatchAddToCart(items, event, cartSnapshotFromCaller = null) {
    if (items.length === 0) return;
    if (this.#blockAddIfPackFieldsIncomplete()) return;

    const stickyCartPresentation = this.#takeStickyCartPresentationPromise();

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const el of cartItemsComponents) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        cartItemComponentsSectionIds.push(el.dataset.sectionId);
      }
    }
    const sections = cartItemComponentsSectionIds.join(',');

    const run = (async () => {
      /** @type {Record<string, unknown> | undefined} */
      let lastCartAddResponse;

      const artieRolesToPurge = /** @type {('system' | 'secondary')[]} */ ([]);
      const purgeSeen = new Set();
      for (const row of items) {
        if (row.artieLineRole === 'system' && !purgeSeen.has('system')) {
          purgeSeen.add('system');
          artieRolesToPurge.push('system');
        }
        if (row.artieLineRole === 'secondary' && !purgeSeen.has('secondary')) {
          purgeSeen.add('secondary');
          artieRolesToPurge.push('secondary');
        }
      }
      /** @type {{ system?: string, secondary?: string }} */
      const keepVariantByRole = {};
      for (const row of items) {
        if (row.artieLineRole === 'system') keepVariantByRole.system = String(row.variantId);
        if (row.artieLineRole === 'secondary') keepVariantByRole.secondary = String(row.variantId);
      }

      const replaceDogIdSet = new Set();
      const replaceDogNameSet = new Set();
      for (const row of items) {
        if (row.artieLineRole !== 'main' || !row.properties) continue;
        const dogId = row.properties._dog_id != null ? String(row.properties._dog_id).trim() : '';
        const dogName = row.properties.dog != null ? String(row.properties.dog).trim() : '';
        if (dogId) replaceDogIdSet.add(dogId);
        else if (dogName) replaceDogNameSet.add(dogName);
      }
      const replaceDogIds = [...replaceDogIdSet];
      const replaceDogNames = [...replaceDogNameSet];

      const needsCartPreload = artieRolesToPurge.length > 0 || replaceDogIds.length > 0 || replaceDogNames.length > 0;
      /** @type {{ items?: unknown[] } | null} */
      let cartPreload = cartSnapshotFromCaller;
      if (needsCartPreload && !cartPreload) {
        cartPreload = await this.#resolveCartSnapshotForMealAtc();
      }

      if (replaceDogIds.length || replaceDogNames.length) {
        await this.#removeArtieMainMealsForDogIdentifiers(
          { dogIds: replaceDogIds, dogNames: replaceDogNames },
          sections,
          cartPreload
        );
      }

      if (artieRolesToPurge.length && cartPreload && Array.isArray(cartPreload.items) && cartPreload.items.length > 0) {
        await this.#removeAllArtieLinesForRoles(artieRolesToPurge, sections, keepVariantByRole, cartPreload);
      }

      /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[]} */
      const itemsPayload = [];
      let batchPostedQuantity = 0;

      for (let i = 0; i < items.length; i++) {
        const line = items[i];
        if (!line) continue;

        const vid = String(line.variantId);
        if (
          line.artieLineRole === 'system' &&
          keepVariantByRole.system === vid &&
          this.#isArtieAddonVariantInCartDom('system', vid)
        ) {
          continue;
        }
        if (
          line.artieLineRole === 'secondary' &&
          keepVariantByRole.secondary === vid &&
          this.#isArtieAddonVariantInCartDom('secondary', vid)
        ) {
          continue;
        }

        /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
        const addRow = {
          id: Number(line.variantId),
          quantity: line.quantity,
        };
        if (line.properties && Object.keys(line.properties).length > 0) {
          addRow.properties = line.properties;
        }
        if (line.artieLineRole === 'main' && line.sellingPlanId) {
          addRow.selling_plan = Number(line.sellingPlanId);
        }

        itemsPayload.push(addRow);
        batchPostedQuantity += line.quantity;
      }

      if (itemsPayload.length === 0) {
        return;
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: itemsPayload,
          sections,
        }),
      });

      const data = await response.json();

      if (stickyCartPresentation) {
        try {
          await stickyCartPresentation;
        } catch {
          // Ignore animation failures; cart state should still update.
        }
      }

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = data.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(data.message));
          }
          this.#setLiveRegionText(data.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: 0,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      lastCartAddResponse = data;

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      /** @type {Record<string, unknown> | undefined} */
      const cartResource =
        lastCartAddResponse && typeof lastCartAddResponse === 'object'
          ? /** @type {Record<string, unknown>} */ (lastCartAddResponse)
          : undefined;

      const itemCountForEvent =
        cartResource && typeof cartResource.item_count === 'number'
          ? cartResource.item_count
          : batchPostedQuantity;

      this.#invalidateMealCartSnapshot();

      this.dispatchEvent(
        new CartAddEvent(cartResource, this.id, {
          source: 'product-form-component',
          itemCount: itemCountForEvent,
          productId: this.dataset.productId,
          sections:
            cartResource && 'sections' in cartResource
              ? /** @type {{ sections?: Record<string, string> }} */ (cartResource).sections
              : undefined,
        })
      );

      if (cartResource && Array.isArray(cartResource.items)) {
        this.#updateCartQuantityFromData(/** @type {Cart} */ (cartResource));
      } else {
        void this.#fetchAndUpdateCartQuantity();
      }
    })();

    run.catch((error) => {
      console.error(error);
    }).finally(() => {
      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
